import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarRange, CheckCircle2, Eye, Loader2, X, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { getToken } from '../../services/tokenService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/date-effet`;
const MAX = 50;
const auth = () => ({ Authorization: `Bearer ${getToken()}` });
const fr = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR') : '—');
const err = (e) => e.response?.data?.detail || 'Erreur';

export const useSelectionLot = () => {
  const [sel, setSel] = useState({});
  const toggle = useCallback((produit, id) => setSel((s) => {
    const k = `${produit}:${id}`;
    if (s[k]) { const { [k]: _, ...r } = s; return r; }
    if (Object.keys(s).length >= MAX) { toast.warning(`${MAX} dossiers maximum par lot`); return s; }
    return { ...s, [k]: { produit, id } };
  }), []);
  const toutes = useCallback((produit, ids, cocher) => setSel((s) => {
    const n = { ...s };
    ids.forEach((id) => { const k = `${produit}:${id}`; if (!cocher) delete n[k]; else if (Object.keys(n).length < MAX) n[k] = { produit, id }; });
    return n;
  }), []);
  return { sel, elements: Object.values(sel), coche: (p, id) => !!sel[`${p}:${id}`], toggle, toutes, vider: () => setSel({}) };
};

const Ligne = ({ l, i, actif, onVoir }) => (
  <tr className={`border-b border-slate-100 ${actif ? 'bg-sky-50' : ''}`} data-testid={`lot-ligne-${i}`}>
    <td className="py-1.5 pr-2"><p className="font-medium text-slate-900">{l.nom || l.reference || l.id}</p><p className="text-[10px] text-slate-500">{l.libelle || l.produit}</p></td>
    <td className="py-1.5 pr-2 text-slate-500">{fr(l.avant?.date_effet)} → {fr(l.avant?.date_fin)}</td>
    <td className="py-1.5 pr-2 font-semibold text-emerald-800" data-testid={`lot-ligne-apres-${i}`}>{l.apres ? `${fr(l.apres.date_effet)} → ${fr(l.apres.date_fin)}` : '—'}</td>
    <td className="py-1.5 pr-2" data-testid={`lot-ligne-etat-${i}`}>
      {l.resultat === 'ok' ? <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Rééditée{l.envoi ? ` · e-mail ${l.envoi === 'envoye' ? 'envoyé' : 'en échec'}` : ''}</span>
        : l.resultat === 'erreur' ? <span className="flex items-center gap-1 text-rose-700" title={l.message}><XCircle className="h-3.5 w-3.5" /> {l.message}</span>
          : l.erreur ? <span className="text-amber-700">Ignoré : {l.erreur}</span> : <span className="text-slate-400">À rééditer</span>}
    </td>
    <td className="py-1.5 text-right">{l.apres && !l.erreur && (
      <button type="button" onClick={() => onVoir(l)} className="rounded border border-slate-200 p-1 text-[#0a3d62] hover:bg-sky-50" title="Aperçu PDF" data-testid={`lot-ligne-apercu-${i}`}><Eye className="h-3.5 w-3.5" /></button>
    )}</td>
  </tr>
);

const LotDialog = ({ elements, open, onOpenChange, onDone }) => {
  const [mode, setMode] = useState('date');
  const [date, setDate] = useState('');
  const [valeur, setValeur] = useState(1);
  const [unite, setUnite] = useState('mois');
  const [lignes, setLignes] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [email, setEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lot, setLot] = useState(null);
  const params = () => ({ elements, mode, date_effet: mode === 'date' ? date : undefined,
    jours: mode === 'decalage' && unite === 'jours' ? Number(valeur) : 0, mois: mode === 'decalage' && unite === 'mois' ? Number(valeur) : 0 });
  const voir = async (l) => {
    setApercu({ cle: `${l.produit}:${l.id}`, url: null });
    try {
      const r = await axios.post(`${API}/${l.produit}/${encodeURIComponent(l.id)}/apercu`, l.apres, { headers: auth(), responseType: 'blob' });
      setApercu({ cle: `${l.produit}:${l.id}`, url: URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })) });
    } catch { setApercu(null); toast.error('Aperçu impossible'); }
  };
  const calculer = async () => {
    setBusy(true); setLignes(null); setApercu(null);
    try {
      const r = await axios.post(`${API}/lot/calcul`, params(), { headers: auth() });
      setLignes(r.data.lignes);
      const premier = r.data.lignes.find((l) => l.apres && !l.erreur);
      if (premier) voir(premier);
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  useEffect(() => { setLignes(null); setApercu(null); }, [mode, date, valeur, unite]);
  useEffect(() => {
    if (!lot || lot.statut !== 'en_cours') return undefined;
    const t = setInterval(() => axios.get(`${API}/lot/${lot.id}`, { headers: auth() }).then((r) => {
      setLot(r.data); setLignes(r.data.lignes);
      if (r.data.statut !== 'en_cours') { toast.success(`${r.data.lignes.filter((l) => l.resultat === 'ok').length} attestation(s) rééditée(s)`); onDone && onDone(); }
    }).catch(() => {}), 2000);
    return () => clearInterval(t);
  }, [lot?.id, lot?.statut]); // eslint-disable-line react-hooks/exhaustive-deps
  const valider = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/lot`, { ...params(), envoyer_email: email }, { headers: auth() });
      setLot({ id: r.data.id, total: r.data.total, traites: 0, statut: 'en_cours' });
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const nb = (lignes || []).filter((l) => l.apres && !l.erreur).length;
  const verrou = !!lot;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto" data-testid="lot-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarRange className="h-5 w-5 text-[#0a3d62]" /> Modifier la date d'effet de {elements.length} dossier(s)</DialogTitle>
          <DialogDescription>Expiration recalculée selon chaque produit (FTPE / RCDS : + 1 an − 1 jour ; DOS-DOMIUM : + 10 ans). Rien n'est modifié avant la validation.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
          <label className="flex items-center gap-1.5"><input type="radio" checked={mode === 'date'} disabled={verrou} onChange={() => setMode('date')} data-testid="lot-mode-date" /> Même date pour tous</label>
          {mode === 'date' && <input type="date" value={date} disabled={verrou} onChange={(e) => setDate(e.target.value)} className="rounded border px-2 py-1" data-testid="lot-date-input" />}
          <label className="flex items-center gap-1.5"><input type="radio" checked={mode === 'decalage'} disabled={verrou} onChange={() => setMode('decalage')} data-testid="lot-mode-decalage" /> Décaler chaque date de</label>
          {mode === 'decalage' && (<>
            <input type="number" value={valeur} disabled={verrou} onChange={(e) => setValeur(e.target.value)} className="w-20 rounded border px-2 py-1" data-testid="lot-decalage-valeur" />
            <select value={unite} disabled={verrou} onChange={(e) => setUnite(e.target.value)} className="rounded border px-2 py-1" data-testid="lot-decalage-unite"><option value="jours">jour(s)</option><option value="mois">mois</option></select>
            <span className="text-xs text-slate-500">(négatif pour avancer)</span>
          </>)}
          <Button size="sm" onClick={calculer} disabled={busy || verrou || (mode === 'date' && !date)} data-testid="lot-calculer-btn">{busy && !lignes ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Calculer les nouvelles dates</Button>
        </div>
        {lignes && (
          <div className="grid gap-3 lg:grid-cols-2">
            <div className="max-h-[50vh] overflow-y-auto">
              <table className="w-full text-xs" data-testid="lot-tableau">
                <thead><tr className="border-b text-left text-slate-500"><th className="pb-1">Dossier</th><th className="pb-1">Avant</th><th className="pb-1">Après</th><th className="pb-1">État</th><th /></tr></thead>
                <tbody>{lignes.map((l, i) => <Ligne key={`${l.produit}-${l.id}`} l={l} i={i} actif={apercu?.cle === `${l.produit}:${l.id}`} onVoir={voir} />)}</tbody>
              </table>
            </div>
            <div className="min-h-[300px] rounded border border-slate-200 bg-slate-50" data-testid="lot-apercu">
              {apercu?.url ? <iframe title="Aperçu" src={apercu.url} className="h-[50vh] w-full rounded" data-testid="lot-apercu-pdf" />
                : apercu ? <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                  : <p className="p-4 text-xs text-slate-500">Cliquez sur l'œil d'une ligne pour voir l'aperçu de son attestation.</p>}
            </div>
          </div>
        )}
        {lignes && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3 text-sm">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={email} disabled={verrou} onChange={(e) => setEmail(e.target.checked)} data-testid="lot-email-checkbox" /> Envoyer la nouvelle attestation à chaque membre par e-mail</label>
            {lot ? (
              <span className="flex items-center gap-2 font-semibold text-[#0a3d62]" data-testid="lot-progression">
                {lot.statut !== 'termine' && <Loader2 className="h-4 w-4 animate-spin" />} {lot.traites || 0} / {lot.total} traité(s){lot.statut === 'termine' ? ' — terminé' : lot.statut === 'interrompu' ? ' — interrompu' : ' (vous pouvez fermer : le traitement continue)'}
              </span>
            ) : (
              <Button onClick={valider} disabled={busy || !nb} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lot-valider-btn">Valider et rééditer {nb} attestation(s)</Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const DateEffetLotBar = ({ selection, onDone }) => {
  const [open, setOpen] = useState(false);
  const [fini, setFini] = useState(false);
  const fermer = (o) => {
    setOpen(o);
    if (!o && fini) { setFini(false); selection.vider(); if (onDone) onDone(); }
  };
  if (!selection.elements.length) return null;
  return (
    <div className="mb-3 flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm" data-testid="lot-barre">
      <span className="font-semibold text-[#0a3d62]" data-testid="lot-compteur">{selection.elements.length} dossier(s) sélectionné(s)</span>
      <Button size="sm" onClick={() => setOpen(true)} className="bg-[#0a3d62] hover:bg-[#0c4a77]" data-testid="lot-ouvrir-btn"><CalendarRange className="mr-1 h-4 w-4" /> Modifier la date d'effet</Button>
      <button type="button" onClick={selection.vider} className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="lot-vider-btn"><X className="h-3.5 w-3.5" /> Vider la sélection</button>
      {open && <LotDialog elements={selection.elements} open={open} onOpenChange={fermer} onDone={() => setFini(true)} />}
    </div>
  );
};
